import React, { useCallback, useRef, useState } from 'react'
import { Button, FlowGrid, Divider, Bleed, Separator, Skeleton, useOverlay } from '@fs/zion-ui'
import { css } from '@emotion/core'
import { ContentFlag } from '@fs/zion-icon'
import transl from './imageQualityAudit.transl'
import { useImageAuditContext } from '../../hooks/useImageAuditContext'
import MultiSelectWindow from './components/MultiSelectWindow'

const containerCss = css`
  padding: 16px;
  box-shadow:
    0px 2px 4px -1px rgba(0, 0, 0, 0.2),
    0px 4px 5px 0px rgba(0, 0, 0, 0.14),
    0px 1px 10px 0px rgba(0, 0, 0, 0.12);
`

export default function ImageQualityAuditFooter() {
  const {
    audit,
    auditComplete,
    loading,
    nextImage,
    previousImage,
    submitAudit,
    stickOnImage,
    getCurrentImageAudit,
    focusedIndex,
  } = useImageAuditContext()

  const buttonRef = useRef()
  const overlay = useOverlay({ focusBackRef: buttonRef })

  const [currentItem, setCurrentItem] = useState(null)

  const openOverlay = useCallback(async () => {
    const { imageAudit } = await getCurrentImageAudit()
    setCurrentItem(imageAudit)

    overlay.open({ target: buttonRef.current })
  }, [getCurrentImageAudit, overlay])

  const currentImage = audit?.[focusedIndex]
  const currentIssues = currentImage?.auditIssues
  const buttonText = currentIssues?.length === 0 ? 'addIssueToImage' : 'editImageQualityIssues'

  return loading ? (
    <>
      <Skeleton.TextInput />
      <Skeleton.Button maxWidth={400} />
      <Separator size="nano" />
      <Skeleton.Button maxWidth={400} />
    </>
  ) : (
    <Bleed sides bottom>
      <div css={containerCss}>
        <FlowGrid gutters="pico">
          <Button
            emphasis="medium"
            fullWidth
            Icon={ContentFlag}
            onClick={openOverlay}
            ref={buttonRef}
            data-testid="open-multi-window"
          >
            {transl[buttonText]()}
          </Button>
          <MultiSelectWindow {...{ overlay, currentItem }} />
          <Separator size="nano" />
          <Divider fullWidth />
          <Button
            fullWidth
            emphasis="medium"
            onClick={nextImage}
            data-testid="next-image-button"
            disabled={stickOnImage}
          >
            {transl.nextImage()}
          </Button>
          <Separator size="xs" />

          <Button
            fullWidth
            emphasis="medium"
            onClick={previousImage}
            data-testid="previous-image-button"
            disabled={stickOnImage}
          >
            {transl.previousImage()}
          </Button>
          <Separator size="xs" />
          <Button
            fullWidth
            emphasis="high"
            onClick={submitAudit}
            disabled={!auditComplete || stickOnImage}
            data-testid="submit-audit-button"
          >
            {transl.submitAudit()}
          </Button>
        </FlowGrid>
      </div>
    </Bleed>
  )
}
